import { Link } from "react-router-dom";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines } from "../components/motion";
import { Calculator } from "../components/Calculator";
import { PlanCards } from "../components/PlanCards";
import { Faq } from "../components/Faq";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { PLANS, CONTRACTOR, money, SITE, orgSchema, faqSchema, offerCatalogSchema, breadcrumbSchema } from "../data/site";
import { PRICING_FAQ } from "../data/faqs";

const compareRows = [
  ["Monthly base fee", "$39", "$49", "Quote required"],
  ["Per person paid", "$5", "$6", "Quote required"],
  ["Prices published online", "Yes", "Yes", "No"],
  ["Multi-state payroll on entry plan", "Included", "Single-state on Simple", "Varies by package"],
  ["Unlimited payroll runs", "Yes", "Yes", "Varies by package"],
  ["Federal, state and local tax filing", "Included", "Included", "Included"],
  ["CPA review before each payroll", "Plus and Complete", "Not a standard feature", "Not a standard feature"],
  ["Setup and migration", "Free", "Free", "Varies"],
];

const addons = [
  ["State tax account registration", "$99 per state, one time"],
  ["Off-cycle and bonus payrolls", "Included"],
  ["Prior-quarter amendments (941-X)", "From $150 per quarter"],
  ["Pay-as-you-go workers' comp", "Quoted through ABSL Insurance (Texas)"],
  ["Year-end W-2 and 1099 filing", "Included on all plans"],
];

const qas = PRICING_FAQ;

const trail = [["Home", "/"], ["Pricing", "/pricing"]];

export default function Pricing() {
  const e = PLANS.essentials;
  return (
    <>
      <Seo
        title="Payroll Pricing from $39/mo + $5/Person | ABSLpay vs Gusto"
        description="ABSLpay payroll plans from $39/month + $5 per person, with multi-state payroll included. Compare prices with Gusto Simple, Plus and Premium and ADP RUN."
        path="/pricing"
        jsonLd={[orgSchema(), offerCatalogSchema(), faqSchema(qas), breadcrumbSchema(trail)]}
      />
      <Breadcrumbs trail={trail} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#ECFDF5_0%,transparent_55%)]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 lg:pt-16 lg:pb-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <MaskedLines
              lines={["Payroll pricing,", "published in full."]}
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-slate-900"
            />
            <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600">
              Three plans, one monthly base fee, and a per-person charge only for people you pay. Every plan includes tax
              filing, direct deposit and payroll in any state.
            </p>
          </div>
          <div className="flex lg:justify-end"><Calculator defaultPlan="plus" /></div>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal><h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Choose a plan</h2></Reveal>
          <div className="mt-12"><PlanCards /></div>
          <Reveal>
            <p className="mt-8 text-sm text-slate-500">
              Contractor-only businesses: {money(CONTRACTOR.base)}/month + {money(CONTRACTOR.per)} per contractor
              ({CONTRACTOR.cname}: {money(CONTRACTOR.cbase)} + {money(CONTRACTOR.cper)}).
            </p>
          </Reveal>
        </div>
      </section>

      <section id="compare">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              ABSLpay compared with Gusto and ADP
            </h2>
            <p className="mt-4 max-w-2xl text-base text-slate-600">
              Entry plans side by side. Gusto figures are from its published pricing; ADP RUN pricing is quote-based.
              Checked {SITE.competitorChecked}.
            </p>
          </Reveal>
          <Reveal className="mt-10 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[640px] text-sm" data-testid="comparison-table">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="p-4 font-bold text-slate-900">Entry plan</th>
                  <th className="p-4 font-bold text-emerald-700">ABSLpay Essentials</th>
                  <th className="p-4 font-bold text-slate-600">Gusto Simple</th>
                  <th className="p-4 font-bold text-slate-600">ADP RUN</th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map(([a, b, c, d]) => (
                  <tr key={a} className="border-b border-slate-100 last:border-0">
                    <th scope="row" className="p-4 text-left font-semibold text-slate-800">{a}</th>
                    <td className="p-4 font-mono-num font-bold text-emerald-700 bg-emerald-50/50">{b}</td>
                    <td className="p-4 text-slate-600">{c}</td>
                    <td className="p-4 text-slate-600">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>

          <Reveal>
            <h3 className="mt-14 font-display text-xl font-bold text-slate-900">What a year costs on the entry plan</h3>
          </Reveal>
          <Reveal className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
            <table className="w-full min-w-[560px] text-sm" data-testid="yearly-cost-table">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="p-4 font-bold text-slate-900">Team size</th>
                  <th className="p-4 font-bold text-emerald-700">ABSLpay per month</th>
                  <th className="p-4 font-bold text-slate-600">Gusto Simple per month</th>
                  <th className="p-4 font-bold text-slate-600">Yearly difference</th>
                </tr>
              </thead>
              <tbody>
                {[3, 10, 25, 50].map((n) => {
                  const ours = e.base + e.per * n;
                  const theirs = e.cbase + e.cper * n;
                  return (
                    <tr key={n} className="border-b border-slate-100 last:border-0">
                      <th scope="row" className="p-4 text-left font-semibold text-slate-800">{n} people</th>
                      <td className="p-4 font-mono-num font-bold text-emerald-700 bg-emerald-50/50">{money(ours)}</td>
                      <td className="p-4 font-mono-num text-slate-600">{money(theirs)}</td>
                      <td className="p-4 font-mono-num font-bold text-slate-900">{money((theirs - ours) * 12)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal><h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Add-ons and one-time fees</h2></Reveal>
          <Reveal className="mt-10 overflow-x-auto rounded-2xl border border-slate-200 bg-[#FAFAFA] max-w-3xl">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="p-4 font-bold text-slate-900">Service</th>
                  <th className="p-4 font-bold text-slate-900">Price</th>
                </tr>
              </thead>
              <tbody>
                {addons.map(([a, b]) => (
                  <tr key={a} className="border-b border-slate-100 last:border-0">
                    <th scope="row" className="p-4 text-left font-semibold text-slate-800">{a}</th>
                    <td className="p-4 text-slate-600">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal><h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Pricing questions</h2></Reveal>
          <div className="mt-10"><Faq qas={qas} testPrefix="pricing-faq" /></div>
          <Reveal>
            <Link to="/contact" data-testid="pricing-quote-btn" className="mt-10 inline-flex rounded-full bg-slate-900 text-white font-bold px-7 py-3.5 hover:bg-emerald-700 transition-colors">
              Get a written quote
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
